import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getListings, getCountries, getCities, getNeighborhoods } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../components/ui/sheet';
import ListingCard from '../components/ListingCard';
import { SlidersHorizontal, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 15;

const ListingsPage = () => {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const [pagination, setPagination] = useState({
    totalListings: 0, totalPages: 1, currentPage: 1, hasNextPage: false, hasPreviousPage: false,
  });

  // Page from URL (SEO-friendly)
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const sort = searchParams.get('sort') || 'recent';

  const filters = useMemo(() => ({
    country: searchParams.get('country') || '',
    city: searchParams.get('city') || '',
    neighborhood: searchParams.get('neighborhood') || '',
    listing_type: searchParams.get('type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    is_furnished: searchParams.get('is_furnished') === '1',
    has_wifi: searchParams.get('has_wifi') === '1',
    has_ac: searchParams.get('has_ac') === '1',
    has_parking: searchParams.get('has_parking') === '1',
  }), [searchParams]);

  // Local UI state mirrors URL but allows pre-apply edits
  const [draft, setDraft] = useState(filters);
  useEffect(() => { setDraft(filters); }, [filters]);

  const listingsRef = useRef(null);

  useEffect(() => {
    getCountries().then(res => setCountries(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (filters.country) {
      getCities(filters.country).then(res => setCities(res.data)).catch(() => {});
    } else {
      // Default to Cameroon's cities so dropdown is always populated
      getCities().then(res => setCities(res.data)).catch(() => {});
    }
  }, [filters.country]);

  useEffect(() => {
    // Load neighborhoods immediately when user picks a city in the FILTER PANEL (draft),
    // not only after applying filters — so suggestions show up on first click.
    const cityValue = draft.city || filters.city;
    if (cityValue && cities.length > 0) {
      const found = cities.find(c => c.city_id === cityValue || c.name === cityValue);
      if (found) {
        getNeighborhoods(found.city_id)
          .then(res => setNeighborhoods(res.data))
          .catch(() => setNeighborhoods([]));
      } else {
        setNeighborhoods([]);
      }
    } else {
      setNeighborhoods([]);
    }
  }, [draft.city, filters.city, cities]);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: PAGE_SIZE, sort };

      if (filters.city) {
        const city = cities.find(c => c.city_id === filters.city || c.name === filters.city);
        params.city = city ? city.name : filters.city;
      }
      if (filters.neighborhood) {
        const nb = neighborhoods.find(n => n.neighborhood_id === filters.neighborhood || n.name === filters.neighborhood);
        params.neighborhood = nb ? nb.name : filters.neighborhood;
      }
      if (filters.listing_type && filters.listing_type !== 'all') params.listing_type = filters.listing_type;
      if (filters.min_price) params.min_price = parseFloat(filters.min_price);
      if (filters.max_price) params.max_price = parseFloat(filters.max_price);
      if (filters.is_furnished) params.is_furnished = true;
      if (filters.has_wifi) params.has_wifi = true;
      if (filters.has_ac) params.has_ac = true;
      if (filters.has_parking) params.has_parking = true;

      const res = await getListings(params);
      setListings(res.data.data || []);
      setPagination(res.data.pagination || { totalListings: 0, totalPages: 1, currentPage: 1, hasNextPage: false, hasPreviousPage: false });
    } catch (error) {
      console.error('Error loading listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sort, filters, cities, neighborhoods]);

  useEffect(() => { loadListings(); }, [loadListings]);

  // Update SEO meta + canonical
  useEffect(() => {
    const baseTitle = language === 'fr' ? 'Logements meublés à louer' : 'Furnished rentals';
    const title = pagination.totalPages > 1
      ? `${baseTitle} — ${language === 'fr' ? 'Page' : 'Page'} ${pagination.currentPage}/${pagination.totalPages} | Roomia`
      : `${baseTitle} | Roomia`;
    document.title = title;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const url = new URL(window.location.href);
    if (pagination.currentPage === 1) url.searchParams.delete('page');
    canonical.href = url.toString();
  }, [pagination, language]);

  const updateUrl = useCallback((updates, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === '' || v === false || v === null || v === undefined) params.delete(k);
      else if (v === true) params.set(k, '1');
      else params.set(k, v);
    });
    if (resetPage) params.delete('page');
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);

  const goToPage = useCallback((p) => {
    if (p < 1 || p > pagination.totalPages || p === pagination.currentPage) return;
    const params = new URLSearchParams(searchParams);
    if (p === 1) params.delete('page'); else params.set('page', String(p));
    setSearchParams(params, { replace: false });
    // Smooth scroll to top of listings
    setTimeout(() => {
      listingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [pagination, searchParams, setSearchParams]);

  const applyFilters = () => {
    updateUrl({
      country: draft.country,
      city: draft.city,
      neighborhood: draft.neighborhood,
      type: draft.listing_type,
      min_price: draft.min_price,
      max_price: draft.max_price,
      is_furnished: draft.is_furnished,
      has_wifi: draft.has_wifi,
      has_ac: draft.has_ac,
      has_parking: draft.has_parking,
    });
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const FilterContent = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-heading font-semibold">{t('location')}</h3>
        <Select value={draft.country || ''} onValueChange={(v) => setDraft(d => ({ ...d, country: v }))}>
          <SelectTrigger data-testid="filter-country"><SelectValue placeholder={t('country')} /></SelectTrigger>
          <SelectContent>
            {countries.map((c) => (<SelectItem key={c.country_id} value={c.country_id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={draft.city || ''} onValueChange={(v) => setDraft(d => ({ ...d, city: v, neighborhood: '' }))}>
          <SelectTrigger data-testid="filter-city"><SelectValue placeholder={t('city')} /></SelectTrigger>
          <SelectContent>
            {cities.map((c) => (<SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={draft.neighborhood || ''} onValueChange={(v) => setDraft(d => ({ ...d, neighborhood: v }))} disabled={!draft.city}>
          <SelectTrigger data-testid="filter-neighborhood"><SelectValue placeholder={t('neighborhood')} /></SelectTrigger>
          <SelectContent>
            {neighborhoods.map((n) => (<SelectItem key={n.neighborhood_id} value={n.neighborhood_id}>{n.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <h3 className="font-heading font-semibold">{t('allTypes')}</h3>
        <Select value={draft.listing_type || ''} onValueChange={(v) => setDraft(d => ({ ...d, listing_type: v }))}>
          <SelectTrigger data-testid="filter-type"><SelectValue placeholder={t('allTypes')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTypes')}</SelectItem>
            <SelectItem value="apartment">{t('apartment')}</SelectItem>
            <SelectItem value="studio">{t('studio')}</SelectItem>
            <SelectItem value="room">{t('room')}</SelectItem>
            <SelectItem value="hotel">{t('hotel')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <h3 className="font-heading font-semibold">{t('priceRange')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" placeholder={t('minPrice')} value={draft.min_price} onChange={(e) => setDraft(d => ({ ...d, min_price: e.target.value }))} data-testid="filter-min-price" />
          <Input type="number" placeholder={t('maxPrice')} value={draft.max_price} onChange={(e) => setDraft(d => ({ ...d, max_price: e.target.value }))} data-testid="filter-max-price" />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-heading font-semibold">{t('amenities')}</h3>
        {[
          { key: 'is_furnished', label: t('furnished'), id: 'furnished' },
          { key: 'has_wifi', label: t('wifi'), id: 'wifi' },
          { key: 'has_ac', label: t('ac'), id: 'ac' },
          { key: 'has_parking', label: t('parking'), id: 'parking' },
        ].map(({ key, label, id }) => (
          <div key={id} className="flex items-center space-x-2">
            <Checkbox id={id} checked={!!draft[key]} onCheckedChange={(v) => setDraft(d => ({ ...d, [key]: v }))} data-testid={`filter-${id}`} />
            <Label htmlFor={id}>{label}</Label>
          </div>
        ))}
      </div>

      <div className="space-y-3 pt-4">
        <Button onClick={applyFilters} className="w-full" data-testid="apply-filters-btn">
          <Search className="h-4 w-4 mr-2" />{t('search')}
        </Button>
        <Button variant="outline" onClick={resetFilters} className="w-full" data-testid="reset-filters-btn">
          {language === 'fr' ? 'Réinitialiser' : 'Reset'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div ref={listingsRef} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="listings-title">
              {t('listings')}
            </h1>
            <p className="text-muted-foreground mt-1" data-testid="listings-count">
              {loading
                ? '...'
                : `${pagination.totalListings} ${language === 'fr' ? 'logements trouvés' : 'listings found'}`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Select value={sort} onValueChange={(v) => updateUrl({ sort: v === 'recent' ? '' : v })}>
              <SelectTrigger className="w-[200px]" data-testid="sort-select">
                <SelectValue placeholder={language === 'fr' ? 'Trier par' : 'Sort by'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">{language === 'fr' ? 'Plus récents' : 'Most recent'}</SelectItem>
                <SelectItem value="price_asc">{language === 'fr' ? 'Prix croissant' : 'Price asc'}</SelectItem>
                <SelectItem value="price_desc">{language === 'fr' ? 'Prix décroissant' : 'Price desc'}</SelectItem>
                <SelectItem value="featured">{language === 'fr' ? 'En vedette' : 'Featured'}</SelectItem>
              </SelectContent>
            </Select>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden gap-2" data-testid="mobile-filter-btn">
                  <SlidersHorizontal className="h-4 w-4" />{language === 'fr' ? 'Filtres' : 'Filters'}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] overflow-y-auto">
                <SheetHeader><SheetTitle>{language === 'fr' ? 'Filtres' : 'Filters'}</SheetTitle></SheetHeader>
                <div className="mt-6"><FilterContent /></div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="flex gap-8">
          <aside className="hidden md:block w-72 shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="font-heading font-semibold text-lg mb-6">{language === 'fr' ? 'Filtres' : 'Filters'}</h2>
                <FilterContent />
              </CardContent>
            </Card>
          </aside>

          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} className="overflow-hidden animate-pulse">
                    <div className="aspect-video bg-muted" />
                    <CardContent className="p-4 space-y-3">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-1/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg">{language === 'fr' ? 'Aucun logement trouvé' : 'No listings found'}</p>
                <Button variant="link" onClick={resetFilters} className="mt-2">
                  {language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-300" data-testid="listings-grid">
                  {listings.map((listing) => (
                    <ListingCard key={listing.listing_id} listing={listing} />
                  ))}
                </div>

                {pagination.totalPages > 1 && (
                  <Pagination
                    pagination={pagination}
                    onPageChange={goToPage}
                    language={language}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

// ===================== Modern, Airbnb-inspired pagination =====================
const Pagination = ({ pagination, onPageChange, language }) => {
  const { currentPage, totalPages, hasPreviousPage, hasNextPage, totalListings, limit } = pagination;

  const buildPageList = () => {
    const pages = [];
    const window_ = 1; // pages around current
    const add = (p) => pages.push(p);
    add(1);
    const start = Math.max(2, currentPage - window_);
    const end = Math.min(totalPages - 1, currentPage + window_);
    if (start > 2) add('…1');
    for (let p = start; p <= end; p++) add(p);
    if (end < totalPages - 1) add('…2');
    if (totalPages > 1) add(totalPages);
    return pages;
  };

  const pageList = buildPageList();
  const firstItem = (currentPage - 1) * limit + 1;
  const lastItem = Math.min(currentPage * limit, totalListings);

  return (
    <nav
      aria-label={language === 'fr' ? 'Pagination' : 'Pagination'}
      className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-6"
      data-testid="pagination"
    >
      <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
        {language === 'fr'
          ? `${firstItem}–${lastItem} sur ${totalListings}`
          : `${firstItem}–${lastItem} of ${totalListings}`}
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-10 w-10 p-0"
          disabled={!hasPreviousPage}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label={language === 'fr' ? 'Page précédente' : 'Previous page'}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {pageList.map((p, idx) =>
          typeof p === 'string' ? (
            <span key={p + idx} className="px-2 text-muted-foreground select-none">…</span>
          ) : (
            <Button
              key={p}
              variant={p === currentPage ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-full h-10 min-w-[2.5rem] px-3 font-medium ${p === currentPage ? 'shadow-sm' : ''}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? 'page' : undefined}
              data-testid={`pagination-page-${p}`}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-10 w-10 p-0"
          disabled={!hasNextPage}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label={language === 'fr' ? 'Page suivante' : 'Next page'}
          data-testid="pagination-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
};

export default ListingsPage;
